/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
/* eslint-disable  */
import { Options } from "react-select";
import { Sex, StorageClass } from "../constant";
import {
  ErrorMessage,
  IConstraintViolationMessage,
  IErrorMessage,
  NestedErrorMessage,
} from "./components/errorMessage";
import { LocalAE } from "./localAE";
import { ApplicationEntity, RemoteAEResponse } from "./ae";
import { IDocument } from "./documents";
import { ISite } from "./site";
import { IPatientIdResponse, IPatientResponse } from "./patient";
import { DropDownResponse, OptionTypeBase } from "./common";
import { SearchPaginateType, TableSortKey } from "./components/datatable";
import {
  ReactSelectOptionGenderProps,
  ReactSelectOptionProps,
} from "./components/reactSelectSearchProps";
import { DateRange } from "./components/datetime";
import {
  IPaginationBaseState,
  Pageable,
  PaginationResponse,
  SlicePageable,
} from "./components/pagination";
import { Issuer } from "./issuer";
import { IOrganisation } from "./organisation";
import { IssuerResponse } from "@advapacs/application-api-protos/dist/Issuer";
import { NexusAssetResponse } from "../types/nexus";
import { IGroup } from "../types/common";
import { Placement } from "react-overlays/esm/usePopper";
import { CriticalResultLightResponse } from "../types/criticalResult";

export interface IStudyResponse {
  id: string;
  description: string;
  studyInstanceUID: string;
  accessionNumber: string | null;
  date: string;
  issuerOfAccessionNumber: string;
  issuerOfPatientId: string;
}

export interface StudySearchParams extends SearchPaginateType {
  patientName?: string;
  patientId?: string;
  patientIssuerIds?: Array<string>;
  patientDob?: string;
  accessionNumber?: string;
  description?: string;
  studyInstanceUid?: string;
  // @deprecated StudyDate
  studyDate?: string;
  studyDateStart?: Date | null;
  studyDateEnd?: Date | null;
  studyReceivedDate?: string;
  modalitiesIds?: Array<string>;
  organisationIds?: Array<string>;
  tagIds?: Array<string>;
  accessionNumberIssuerIds?: Array<string>;
  storageClasses?: Array<string>;
  referringDoctor?: string;
  orderingProviderName?: string;
  localApplicationEntityId?: string;
  remoteApplicationEntityId?: string;
  patientIdOrPatientName?: string;
  siteIds?: Array<string>;
  statuses?: string[];
  studyReceivedDateStart?: Date | null;
  studyReceivedDateEnd?: Date | null;
  studyFolderId?: string;
  isFuzzySearch?: boolean;
  dataSetIds?: Array<string>;
  dataSourceId?: string;
  sortKeys?: TableSortKey[];
  studyStatusIds?: string;
  isValidated?: boolean;
  localAeIds?: Array<string>;
  remoteAeIds?: Array<string>;
  enforceSearchRestriction?: boolean;
}

export interface RoutingQueueSearchParams extends SearchPaginateType {
  patientName?: string;
  patientId?: string;
  genders?: Array<string>;
  patientDob?: string;
  accessionNumber?: string;
  studyDescription?: string;
  studyDate?: string;
  patientIdsIssuer?: string;
  accessionNumbersIssuer?: string;
  localApplicationEntityIds?: Array<string> | [];
  remoteApplicationEntityIds?: Array<string> | [];
}

export type RoutingQueueSearchState = {
  patientName: string;
  patientId: string;
  genders?: Array<ReactSelectOptionGenderProps>;
  patientDob?: Date;
  accessionNumber: string;
  studyDescription: string;
  studyDate?: DateRange;
  studyReceivedDate?: Date;
  patientIdsIssuer?: OptionTypeBase;
  accessionNumbersIssuer?: OptionTypeBase;
  localApplicationEntityIds: OptionTypeBase[];
  remoteApplicationEntityIds: OptionTypeBase[];
};

export interface UploadQueueSearchParams extends SearchPaginateType {
  patientName?: string;
  patientId?: string;
  genders?: Array<string>;
  patientDob?: string;
  accessionNumber?: string;
  studyDescription?: string;
  studyDate?: string;
  localApplicationEntityIds?: string;
  remoteApplicationEntityIds?: string;
  gatewayIds?: string;
}

export type UploadQueueSearchState = {
  patientName: string;
  patientId: string;
  genders?: Array<ReactSelectOptionGenderProps>;
  patientDob?: Date;
  accessionNumber: string;
  studyDescription: string;
  studyDate?: DateRange;
  studyReceivedDate?: Date;
  localApplicationEntityIds: OptionTypeBase[];
  remoteApplicationEntityIds: OptionTypeBase[];
  gatewayIds: OptionTypeBase[];
};

export interface SearchState {
  storageClass: Array<ReactSelectOptionProps> | [];
  patientName: string;
  patientId: string;
  accessionNumber: string;
  patientIssuers?: ReactSelectOptionProps | null;
  accessionNumberIssuers?: ReactSelectOptionProps | null;
  patientDob?: Date;
  description: string;
  studyDate?: DateRange;
  studyReceivedDate?: DateRange;
  referringDoctor: string;
  modalities: Array<ReactSelectOptionProps> | [];
  organisation: Array<ReactSelectOptionProps> | [];
  site: OptionTypeBase | Options<OptionTypeBase> | null;
  statuses: Array<ReactSelectOptionProps> | [];
}

export interface SearchFormProps {
  onSearch: (params: StudySearchParams, initPagination?: boolean) => void;
  closeSidebar: () => void;
  isMigrationJobStudy?: boolean;
  onReset?: (params: StudySearchParams, initPagination?: boolean) => void;
}

export interface RoutingQueueSearchFormProps {
  onSearch: (params: RoutingQueueSearchParams) => void;
}

export interface PaginateState {
  numberOfElements: number;
  offset: number;
  pageNumber: number;
  last: boolean;
  first: boolean;
  totalElements: number;
  size?: number;
}

export interface MoveStudyModel {
  loading: boolean;
  isMoved: boolean;
  errorMessage: ErrorMessage | null;
}

export interface DicomAttributeResponse {
  [key: string]: {
    vr: string;
    Value: (string | DicomAttributeResponse | null)[];
  };
}

export interface DicomTagDisplay {
  tag?: string;
  type?: string;
  value?: string;
  name?: string;
  children?: DicomTagDisplay[];
  parent?: DicomTagDisplay;
  visible?: boolean;
}

export interface PhoneVerificationState {
  readyVerifyCode?: boolean;
  loading?: boolean;
  errorMessage?: IErrorMessage;
  verifyCodeSuccessful?: boolean;
  studyIds?: string[];
  verifyingStudy?: IStudyDetail;
}
export interface IStudyState {
  deletedMessage: string;
  errorMessage: IErrorMessage | IConstraintViolationMessage | undefined | null;
  breakGlassErrorMessage:
    | IErrorMessage
    | IConstraintViolationMessage
    | undefined
    | null;
  loading: boolean;
  accessGrantLoading?: boolean;
  loadingStudyDetail: boolean;
  loadingSeriesDetail: boolean;
  studies: Array<IStudyDetail>;
  studiesAccessGrants: Array<IBreakGlassPatientResponse>;
  moveStudyModel: MoveStudyModel;
  isCascading: boolean;
  studyDetail: IStudyDetail;
  patientDetail: IPatientResponse;
  seriesDetail: ISeriesDetail;
  series: Array<ISeriesDetail> | null;
  allSeries?: Array<ISeriesDetail> | null;
  pageableStudyAccessGrant: SlicePageable;
  pageableStudy: SlicePageable;
  pageableSeries: Pageable;
  pageableInstance: Pageable;
  instances: Array<Instance> | null;
  instanceHeader: Array<DicomAttributeResponse>;
  needReloadStudies: boolean;
  finishedLoadSeries: boolean;
  finishedLoadInstances: boolean;
  documents: PaginationResponse<IDocument>;
  loadingDocuments: boolean;
  viewSharesListStudy: PaginationResponse<ShareStudy>;
  loadingViewSharesListStudy: boolean;
  isOnlySearchingSeries: boolean;
  zedLoading: boolean;
  reportDetail?: IDiagnosticReport | IClinicianDiagnosticReport;
  accessDeniedErrorMessage?: string;
  downloading?: boolean;
  showPatientVerifySmsModal?: boolean;
  phoneVerification?: PhoneVerificationState;
}

export interface IRoutingQueueState {
  errorMessage: NestedErrorMessage | null;
  loading: boolean;
  deleting: boolean;
  updating: boolean;
  paginate: PaginateState;
  pageable: Pageable;
  instances: IRoutingQueueDetail[];
  intervalSeconds: number;
  fetchCount: number;
  showPriorityModal: boolean;
  showDeleteModal: boolean;
}

export interface IUploadQueueState {
  errorMessage: NestedErrorMessage | null;
  loading: boolean;
  deleting: boolean;
  updating: boolean;
  paginate: PaginateState;
  pageable: Pageable;
  instances: IRoutingQueueDetail[];
  intervalSeconds: number;
  fetchCount: number;
  searchParams: UploadQueueSearchParams | null;
  localApplicationEntityIds?: Array<string> | [];
  remoteApplicationEntityIds?: Array<string> | [];
  gatewayIds?: Array<string> | [];
}

export interface StudySize {
  studyId: string;
  size: number;
}

export interface StudyQR {
  relatedSeriesCount?: number;
  relatedInstancesCount?: number;
  modalitiesInStudy?: Array<string>;
  anySeriesHidden?: boolean;
}

export interface SeriesQR {
  relatedInstancesCount?: number;
}

export interface ICustomerResponse {
  id?: string;
  name?: string;
  status?: string;
}

export interface IBreakGlassPatientResponse {
  id?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dob?: string;
  sex?: string;
  patientIds?: IPatientIdResponse[];
  deceased?: boolean;
  deceasedAt: string;
  createdAt: string;
  updatedAt: string;
  clinicianPortal?: ICustomerResponse & {
    breakGlassRequiresJustification?: boolean;
  };
}

export interface TagResponse {
  id: string;
  tagKey: string;
  tagValue: string;
}

export interface IStudyOrderValidate {
  id: string;
  orderId: string;
  patientId: string;
}

export interface IStudyDetail {
  id: string;
  description: string;
  studyInstanceUid: string;
  studyDescription?: string;
  accessionNumber: string | null;
  date: string;
  dateTime?: Date | string;
  instances?: Array<Instance> | [];
  time: string;
  studyId: string | null;
  qr: StudyQR;
  createdAt: string;
  updatedAt?: string;
  studyDate?: string;
  studyDateTime?: string;
  deletedAt?: string;
  storage: DropDownResponse<string>;
  size?: number;
  referrer: string;
  referringDoctor: string;
  orderingProviderName?: string;
  patient: IPatientResponse;
  accessionNumberIssuer?: Issuer;
  committed: string;
  storageClasses: StorageClass[];
  isOrganisationFiltered?: boolean;
  // TOTO: // remove old prop report
  report?: boolean; //Deprecated for hasReport
  hasReport?: boolean;
  hasOrder?: boolean;
  clinicianPortal?: ICustomerResponse;
  sensitive?: boolean;
  orderId?: string;
  deletionProtected?: boolean;
  tags?: TagResponse[];
  dataSet?: DropDownResponse<string>;
  taskInfo?: {
    completedCount: number;
    totalCount: number;
  };
  studyStatus?: DropDownResponse<string>;
  criticalResult?: CriticalResultLightResponse;
  stableAt?: string;
  operatorNames?: string[];
}

export interface IPractitioner {
  id: string;
  practitionerId: string;
  firstName: string;
  lastName: string;
  suffix?: string;
  prefix?: string;
  middleName?: string;
  practitionerIdIssuer?: IssuerResponse;
  name?: IHumanName[];
}

export interface IClinicianPractitioner {
  id?: string;
  name?: IHumanName[];
}

export interface IPractitionerSearch extends IPaginationBaseState {
  searchKeyword?: string;
  issuerIds?: string[];
  practitionerId?: string;
  status?: boolean | undefined;
}

export interface IReportDistribution {
  email: boolean;
}

export interface IPractitionerManageRequest {
  id?: string;
  practitionerId?: string;
  sex?: string;
  dob?: Date | string;
  pid?: string;
  practitionerIdIssuerId?: string;
  lastName?: string;
  firstName?: string;
  middleName?: string;
  suffix?: string;
  prefix?: string;
  practitionerIdIssuer?: IssuerResponse;
  name?: IHumanName[];
  mobilePhone?: string;
  email?: string;
  reportDistribution?: IReportDistribution;
  countryPrefix?: OptionTypeBase | null;
  enabled?: boolean;
  type?: string;
}

export const defaultPractitionersValue: IPractitionerManageRequest = {
  id: "",
  practitionerId: "",
  sex: "",
  dob: "",
  pid: "",
  practitionerIdIssuerId: "",
  lastName: "",
  firstName: "",
  middleName: "",
  suffix: "",
  prefix: "",
  mobilePhone: "",
  email: "",
  reportDistribution: {
    email: false,
  },
  countryPrefix: null,
  enabled: true,
};

export interface IHumanName {
  id: string;
  practitionerId: string;
  firstName: string;
  lastName: string;
  middleName: string;
  suffix: string[];
  prefix: string[];
  name: string;
}

export interface IDiagnosticReport {
  id: string;
  status: string;
  issuedAt: Date;
  principalResultInterpreter?: IPractitioner;
  transcriptionist?: IPractitioner;
  assistantResultInterpreters?: IPractitioner[];
  contents: IDiagnosticReportContent[];
  referrer?: IPractitioner;
  generatePdf?: boolean;
  studyDate?: Date | string;
  modalities?: string[];
  accessionNumbers?: string[];
  accessionNumberIssuer?: Issuer;
  description?: string;
  // The report's linked studies, most recent first by the study's own date;
  // worklist-api breaks ties by study UID, then data set (AX-8426)
  studies?: IReportStudy[];
}

// A study linked to a report, as AdvaView identifies it
export interface IReportStudy {
  studyInstanceUid: string;
  dataSetId: string;
  studyDate?: string;
}

export interface IClinicianDiagnosticReport {
  id: string;
  status: string;
  issuedAt: Date;
  principalResultInterpreter?: IPractitioner;
  transcriptionist?: IPractitioner;
  contents: IDiagnosticReportContent[];
  referrer?: IPractitioner;
  generatePdf?: boolean;
}

export interface DiagnosticReportContentResponse {
  id: string;
  contentType: string;
  size?: number;
  htmlPlainTextCompatible?: boolean;
}
export interface IDiagnosticReportContent
  extends DiagnosticReportContentResponse {
  contentEncoding: string;
  content: string;
  tag?: string;
  needRenderPdf?: boolean;
  reportId?: string;
  patientId?: string;
}

export interface IStudyIndexedResponse extends IStudyDetail {
  status?: string;
}

export interface DeleteFailed {
  patientId: string;
  studyId: string;
  seriesId?: string;
  instanceId?: string;
}

export interface IRoutingQueueDetail {
  id: string;
  completed: number;
  remaining: number;
  priority: number;
  createdAt: string;
  availableAt: string;
  attempts: number;
  failed: number;
  study: IStudyDetail;
  localApplicationEntity: ApplicationEntity;
  destinationApplicationEntity: ApplicationEntity;
}

export interface IUploadQueueDetail {
  id: string;
  gatewayId?: string;
  studyInstanceUid: string;
  instanceCount: number;
  priority: number;
  queuedAt: string;
  patient: Patient;
  study: IStudyDetail;
  localApplicationEntity: ApplicationEntity;
  remoteApplicationEntity: ApplicationEntity;
}

export interface MimaPatientProps {
  patientIds: IPatientIdResponse[];
  issuerEnabled?: boolean;
  preventClickToView?: boolean;
  maxWidth?: string | number;
  placement?: Placement;
  widthStyle?: string;
}

export interface MimaAccessionNumberProps {
  accessionNumber?: string;
  issuer?: Issuer;
  issuerEnabled?: boolean;
  renderText?: (asn: string) => React.ReactElement | React.ReactElement[];
  placement?: Placement;
}

export interface InstanceHierarchyResponse {
  patientId: string;
  studyId: string;
  seriesId: string;
}

export interface Instance {
  id: string;
  number: number;
  numberOfFrames: number;
  instanceNumber: string;
  sopInstanceUid: string;
  sopClassUid: string;
  contentDate: string;
  contentTime: string;
  contentDateTime: string;
  path: string;
  size: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  committed: boolean;
  storageClasses: StorageClass[];
}

export interface RelationalInstanceResponse {
  instance: Instance;
  hierarchy: InstanceHierarchyResponse;
}

export interface StudyQueryRetrieve {
  seriesId: string;
  patientId?: string;
  relatedStudies?: number;
  relatedSeries?: number;
  relatedInstances?: number;
  studyId?: string;
  modalitiesInStudy?: Array<string>;
}

export interface Patient {
  id: string;
  patientId: string;
  name: string;
  sex: Sex;
  dob: string;
  qr: StudyQueryRetrieve;
  createdAt: string;
  updatedAt: string;
  size?: StudySize;
  localApplicationEntities?: Array<LocalAE>;
  sourceApplicationEntities?: Array<RemoteAEResponse>;
  organisation: string;
  referrer: string;
}

export interface ISeriesDetail {
  date: string;
  department: null;
  description: string;
  id: string;
  institution: string;
  modality: string;
  number: number;
  qr: SeriesQR;
  relatedInstances: number;
  seriesId: string;
  seriesInstanceUID: string;
  time: string;
  dateTime: string;
  committed: string;
  storageClasses: StorageClass[];
  deletedAt?: string;
  organisation?: IOrganisation;
  site?: ISite;
  localApplicationEntity?: LocalAE;
  sourceApplicationEntity?: RemoteAEResponse;
  visibleInPatientPortal?: boolean;
  visibleInClinicianPortal?: boolean;
  operatorNames?: string[];
}

export interface IMoveDetail {
  localAE: OptionTypeBase | undefined;
  destinationApplicationEntity: OptionTypeBase | undefined;
  priority: number;
  isTriggerTransformRules: boolean;
  isTriggerCompressionRules: boolean;
}

export interface IUpdateMoveDetail {
  localAE: string;
  destinationApplicationEntity: string;
  priority: number;
  isTriggerTransformRules: boolean;
  isTriggerCompressionRules: boolean;
}

export const defaultPatientDetail: Readonly<IPatientResponse> = {
  id: "",
  patientIds: [],
  firstName: "",
  lastName: "",
  middleName: "",
  dob: "",
  sex: "",
};

export const defaultStudyQR: Readonly<StudyQR> = {
  relatedSeriesCount: 0,
  relatedInstancesCount: 0,
  modalitiesInStudy: [],
};

export const defaultStudyDetail: Readonly<IStudyDetail> = {
  createdAt: "",
  id: "",
  description: "",
  studyInstanceUid: "",
  accessionNumber: "",
  date: "",
  time: "",
  studyId: "",
  qr: defaultStudyQR,
  size: 0,
  storage: {
    id: "",
    name: "",
  },
  referrer: "",
  referringDoctor: "",
  patient: {
    id: "",
    dob: "",
    firstName: "",
    middleName: "",
    lastName: "",
    patientIds: [],
    sex: "",
  },
  accessionNumberIssuer: {} as Issuer,
  committed: "",
  storageClasses: [],
};

export const defaultSeriesDetail: Readonly<ISeriesDetail> = {
  date: "",
  department: null,
  description: "",
  id: "",
  institution: "",
  modality: "",
  number: 0,
  relatedInstances: 0,
  dateTime: "",
  seriesId: "",
  seriesInstanceUID: "",
  time: "",
  qr: {
    relatedInstancesCount: 0,
  },
  committed: "",
  storageClasses: [],
  organisation: {} as IOrganisation,
  site: {} as ISite,
};

export const defaultMoveDetail: Readonly<IMoveDetail> = {
  localAE: undefined,
  destinationApplicationEntity: undefined,
  priority: 50,
  isTriggerTransformRules: false,
  isTriggerCompressionRules: false,
};

export interface IUpdatePatientId {
  id?: string;
  pid?: string;
  issuerId?: string;
}

export interface IUpdatedPatientRequest {
  patientName?: string;
  dob?: string;
  sex?: string;
  patientIds: Array<IUpdatePatientId>;
  deceased?: boolean;
  deceasedAt?: Date | string;
}
export interface IUpdatedStudyRequest {
  description?: string;
  accessionNumber?: string | null;
  studyDate?: string;
  studyTime?: string;
  referringDoctor?: string;
  accessionNumberIssuerId?: string;
  sensitive?: boolean;
}

export interface IUpdatedSeriesRequest {
  number?: string;
  description?: string;
  date?: string;
  time?: string;
}

export interface ShareStudy {
  id: string;
  token?: string;
  toEmail: string;
  expiresAt: string;
  status: string;
  revokedAt: string;
  viewer?: string;
  createdAt: string;
  hasPassword?: boolean;
}

export interface ShareStudyState {
  viewer?: string;
  expiresAt?: Date;
  toEmail?: string;
  defaultIssuerOfPatientIdOption?: OptionTypeBase;
  password?: string;
}

export interface IStudyLightDiagnosticReport {
  id?: string;
  studyInstanceUid?: string;
  storageClasses?: Array<string>;
  sensitive?: boolean;
}

export enum DownloadStudyTypeCommon {
  DICOM = "DICOM",
  JPEG = "JPEG",
}

export interface DownloadStudyModalCommonRequest {
  issuerOfPatientIdId?: string;
  issuerOfAccessionNumberId?: string;
  decompress?: boolean;
  mode?: string;
  viewer?: NexusAssetResponse;
  includeReport?: boolean;
  anonymize?: IGroup<boolean>;
}

export interface IDownloadStudyModalCommonProps {
  title?: string;
  show: boolean;
  issuerEnabled?: boolean;
  patientIds: IPatientIdResponse[];
  accessionNumber?: string;
  accessionNumberIssuer?: Issuer;
  isMulti?: boolean;
  isIncludeReport?: boolean;
  dicomViewers?: Array<NexusAssetResponse>;
  issuersAccessionNumber?: Array<OptionTypeBase>;
  issuersPatientId?: Array<OptionTypeBase>;
  enabledPortableViewer?: boolean;
  isDark?: boolean;
  handleDownload: (data: DownloadStudyModalCommonRequest) => void;
  onClose: () => void;
  isVisibleSeries?: boolean;
}

export interface IMergeStudyProps {
  study?: OptionTypeBase & IStudyDetail;
  show: boolean;
  onClose?: (isReload: boolean) => void;
  onMerge?: (patient: IStudyDetail, destination: IStudyDetail) => void;
}

export interface IStudyNote {
  id?: string;
  createdAt?: Date;
  content?: string;
  createdBy?: string;
  type?: string;
  updatedAt?: Date;
  author?: string;
  owner?: boolean;
}
